// Per-screen error boundary (docs/miniapp-m7-tz.md §3.3).
//
// Three layers of failure handling, this is the middle one:
//   1. useQuery `isError`     — a *fetch* failed → the screen renders its own
//                               retry state (already in every screen).
//   2. RouteErrorBoundary     — a *render* threw (bad payload, a bug) → catch it
//                               at the route so only this screen dies, the bottom
//                               nav survives, and "Попробовать снова" recovers in
//                               place by invalidating queries + remounting.
//   3. Sentry.ErrorBoundary   — last resort in main.tsx → full-app CrashFallback.
//
// React error boundaries must be class components; the functional `RouteBoundary`
// wrapper exists only to pull `useQueryClient`/`useLocation` in. Keying the inner
// boundary by pathname means navigating away from a crashed screen resets it for
// free — you never get stuck on a dead route.
import { Component, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'

import { Sentry } from '../sentry'
import { Button, Icon, t } from '../design'

interface BoundaryProps {
  children: ReactNode
  /** Recover: invalidate the screen's queries so the remount refetches fresh. */
  onReset: () => void
}

class Boundary extends Component<BoundaryProps, { error: Error | null }> {
  state = { error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    // A render crash the per-screen `isError` path can't see — report it so the
    // §6 acceptance ("≥90% errors caught by ErrorBoundary") has signal.
    try {
      Sentry.captureException(error, { extra: { componentStack: info?.componentStack } })
    } catch {
      /* Sentry not configured — degrade silently. */
    }
  }

  private retry = () => {
    this.props.onReset()
    this.setState({ error: null })
  }

  render() {
    if (this.state.error) return <ScreenErrorFallback onRetry={this.retry} />
    return this.props.children
  }
}

function ScreenErrorFallback({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      className="scroll-y"
      style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '64px 24px', textAlign: 'center' }}
    >
      <span style={{ width: 72, height: 72, borderRadius: 18, background: 'var(--warning-bg)', color: 'var(--warning)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon.AlertTri size={30} sw={2} />
      </span>
      <div className="t-h2">{t('err.boundary.title')}</div>
      <div className="t-caption" style={{ maxWidth: 280 }}>{t('err.boundary.body')}</div>
      <Button variant="secondary" icon={<Icon.Refresh size={16} sw={2} />} onClick={onRetry} style={{ marginTop: 4 }}>
        {t('btn.retry')}
      </Button>
    </div>
  )
}

/** Wrap the routed area. `key={pathname}` gives per-screen isolation + a free
 *  reset on navigation; manual retry invalidates all queries so the remounted
 *  screen refetches. */
export function RouteBoundary({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const { pathname } = useLocation()
  return (
    <Boundary key={pathname} onReset={() => void qc.invalidateQueries()}>
      {children}
    </Boundary>
  )
}
